import PropTypes from "prop-types";
import * as React from "react";
import { useCurrentUser } from "../../globalState";
import {
  useActiveInfluencer,
  useProductForShopBuilder,
  useProductTypesForUser,
} from "../../hooks";
import { Alert, Spinner } from "../../themeV2";
import ComposerShell from "./ComposerShell";
import CreateComposer from "./create/CreateComposer";
import EditComposer from "./edit/EditComposer";
import useComposerClose from "./useComposerClose";

const StatusShell = function StatusShell({ title, isError, onClose }) {
  const closing = useComposerClose({ isDirty: false, onClose });

  return (
    <ComposerShell
      open={closing.open}
      onRequestClose={closing.requestClose}
      onClosed={closing.onClosed}
      title={title}
    >
      {isError ? (
        <Alert severity="error">
          This product couldn&apos;t be loaded. Close and try again.
        </Alert>
      ) : (
        <Spinner label="Loading product" />
      )}
    </ComposerShell>
  );
};

StatusShell.propTypes = {
  isError: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string.isRequired,
};

const shopFrom = (influencer) => ({
  influencerId: influencer.influencerId,
  name: influencer.name,
  avatarUrl: influencer.avatarUrl,
  isDomestic: influencer.isDomestic,
  productTagString: influencer.productTagString ?? null,
});

const existingProductsOf = (influencer) =>
  (influencer.products ?? []).map((product) => ({
    productId: product.productId,
    name: product.name,
    signedType: product.signedType,
  }));

export default function ProductComposer({
  productId,
  onClose,
  livestreamWarning,
}) {
  const activeInfluencer = useActiveInfluencer();
  const currentUser = useCurrentUser();
  const isEditing = productId !== undefined && productId !== null;
  const productQuery = useProductForShopBuilder(isEditing ? productId : null);
  const types = useProductTypesForUser();

  const shop = React.useMemo(
    () => shopFrom(activeInfluencer),
    [activeInfluencer],
  );
  const existingProducts = React.useMemo(
    () => existingProductsOf(activeInfluencer),
    [activeInfluencer],
  );

  const title = isEditing ? "Edit product" : "Add products";
  const isLoading = productQuery.isLoading || types.isLoading;
  const isError = productQuery.isError || types.isError;
  const [openedWhileLoading] = React.useState(isLoading || isError);

  const shared = {
    shop,
    productTypes: types.productTypes,
    otherBumpPercentage: types.otherBumpPercentage,
    otherImageRequirements: types.otherImageRequirements,
    existingProducts,
    isAdmin: Boolean(currentUser.isAdmin || currentUser.isSuperadmin),
    user: currentUser,
    appearInstantly: openedWhileLoading,
    onClose,
  };

  return isLoading || isError ? (
    <StatusShell title={title} isError={isError} onClose={onClose} />
  ) : isEditing ? (
    <EditComposer
      {...shared}
      product={productQuery.product}
      livestreamWarning={Boolean(livestreamWarning)}
    />
  ) : (
    <CreateComposer {...shared} />
  );
}

ProductComposer.propTypes = {
  livestreamWarning: PropTypes.any,
  onClose: PropTypes.func,
  productId: PropTypes.number,
};
